import React, { useEffect, useMemo, useRef, useState } from "react";

import { BoardPiece } from "./BoardPiece";
import { PreviewPlacement } from "./PreviewPlacement";
import { boardBounds, cube2cartesian } from "../../game/geometry";
import { Hex, Piece } from "../../game/mechanics";
import { useApp } from "../../contexts/app";

export const Board = ({ pieces }: { pieces: Piece[] }) => {
  const {
    state: { game },
    dispatch,
  } = useApp();

  const boardRef = useRef<HTMLDivElement>(null);

  const [boardSize, setBoardSize] = useState({ W: 0, H: 0 });

  const updateBoardSize = () => {
    console.log("UPDATE BOARD");

    if (boardRef.current) {
      setBoardSize({
        W: boardRef.current.clientWidth,
        H: boardRef.current.clientHeight,
      });
    }
  };

  // Trigger initial board display
  useEffect(() => {
    updateBoardSize();
  }, [boardRef, game.board.pieces.length]);

  // Handle resizing
  useEffect(() => {
    let doit: NodeJS.Timeout;
    const resizeListener = () => {
      clearTimeout(doit);
      doit = setTimeout(updateBoardSize, 150);
    };
    window.addEventListener("resize", resizeListener, false);
    return () => {
      // cleanup
      window.removeEventListener("resize", resizeListener);
    };
  }, []);

  // Compute piece cartesian coordinates and board bounds
  const piecesWithCoords = pieces.map((p) => ({
    ...p,
    cartesian: cube2cartesian(p.hex),
  }));
  const { bounds, w, h } = boardBounds(
    piecesWithCoords.map((p) => p.cartesian)
  );

  // Container div size
  const W = boardSize.W;
  const H = boardSize.H;

  // Take smallest scale factor to fit while conserving aspect ratio
  const xscale = W / w;
  const yscale = H / h;
  const scale = Math.min(xscale, yscale);

  const offsetInDiv = {
    X: xscale > yscale ? (W - w * scale) / 2 : 0,
    Y: xscale < yscale ? (H - h * scale) / 2 : 0,
  };

  const allowPlaying =
    !game.onlineGameId ||
    (!!game.onlineGameId && game.localPlayerId === game.turnOfPlayer.id);

  const previewPlacements = useMemo((): Hex[] => {
    if (game.action.previewingMoveFor) {
      return game.board.legalMovements(game.action.previewingMoveFor);
    } else if (game.action.placing) {
      return game.board.legalPlacements(game.turnOfPlayer.id);
    } else if (game.action.moving) {
      const legal = game.board.legalMovements(game.action.moving);
      return legal;
    } else return [];
  }, [
    game.action.previewingMoveFor,
    game.action.placing,
    game.action.moving,
    game.board,
    game.turnOfPlayer.id,
  ]);

  return (
    <div
      className="h-100 d-flex flex-column position-relative"
      ref={boardRef}
      onClick={() => {
        dispatch({ type: "CANCEL_ACTION" });
      }}
    >
      {game.board.pieces.length === 0 && (
        <PreviewPlacement
          key={`00`}
          hex={new Hex(0, 0)}
          transform={{
            scale,
            min: {
              x: bounds.xmin,
              y: bounds.ymin,
            },
            offsetInDiv,
          }}
        />
      )}
      {piecesWithCoords
        .sort((a, b) => a.h - b.h)
        .map((p) => (
          <BoardPiece
            key={`${p.hex.q}${p.hex.r}${p.h}`}
            piece={p}
            transform={{
              scale,
              min: {
                x: bounds.xmin,
                y: bounds.ymin,
              },
              offsetInDiv,
            }}
            clickable={allowPlaying}
          />
        ))}
      {allowPlaying &&
        previewPlacements.map((hex) => (
          <PreviewPlacement
            key={`${hex.q}${hex.r}`}
            hex={hex}
            transform={{
              scale,
              min: {
                x: bounds.xmin,
                y: bounds.ymin,
              },
              offsetInDiv,
            }}
          />
        ))}
    </div>
  );
};
